import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Button, Card, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as moment from 'moment';
import { Navigate, useParams } from 'react-router-dom';
import Title from '../../common/page-title';
import ToastMessage from '../../common/toast-message';
import { initialStates } from '../../../initialStates';
import DelinkForm from './delink-form';
import ConfirmationField from './confirmation-row';
import { delinkOutlet, getAdminOutlet, getCustomerNumbers } from '../../../actions/outlet';

import './delink.css';

// Delink wizard page. Migrated to a function component alongside its form
// (delink-form.jsx): a page component wrapping a form component is the project's
// standard shape, and both convert together.
export default function DelinkPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const store = useStore();

  const outlets = useSelector(state => state.customer_numbers.outlets);
  const fetched = useSelector(state => state.customer_numbers.fetched);
  const outlet = useSelector(state => state.store.outlet);
  const outletFetched = useSelector(state => state.store.fetched);
  const outletFetching = useSelector(state => state.store.fetching);
  const errors = useSelector(state => state.store.errors);
  const delink = useSelector(state => state.delink);

  const [step, setStep] = useState(1);
  const [formOutlet, setFormOutlet] = useState({});
  const [form, setForm] = useState(initialStates.admin_delink);

  // Mount: clear any prior delink state, then load outlets (and, when arriving
  // with an :id, that outlet's customer numbers). Reads the just-fetched outlet
  // straight from the store, matching the old constructor + willMount.
  useEffect(() => {
    dispatch({ type: 'CLEAR_OUTLET_DELINK' });

    if (id !== undefined) {
      dispatch(getAdminOutlet(id)).then(() => {
        const loaded = store.getState().store.outlet;
        dispatch(getCustomerNumbers(loaded.attributes['customer-number'], '', false, true));
      });
    } else {
      dispatch(getCustomerNumbers('', '', false, true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to step 1 when the :id is cleared or a delink just completed
  // (replaces UNSAFE_componentWillReceiveProps).
  const prevId = useRef(id);
  const prevDelinked = useRef(delink.delinked);
  useEffect(() => {
    const idCleared = id !== prevId.current && !id;
    const justDelinked = delink.delinked && !prevDelinked.current;
    if (idCleared || justDelinked) {
      setStep(1);
      setFormOutlet({});
      setForm(initialStates.admin_delink);
      dispatch({ type: 'CLEAR_OUTLET_DELINK' });
    }
    prevId.current = id;
    prevDelinked.current = delink.delinked;
  }, [id, delink.delinked, dispatch]);

  // Surface a not-found outlet then redirect to the outlets list.
  const notFound = typeof errors === 'string' && errors === 'Not Found';
  useEffect(() => {
    if (notFound) {
      toast.error(<ToastMessage iconStyle="error" message={`Outlet not found by id ${id}`}/>);
    }
  }, [notFound, id]);

  const handleSubmit = (payLoad) => {
    if (payLoad.outlet === '') {
      toast.error(<ToastMessage iconStyle="error" message="You have to select an outlet first!"/>);
      return false;
    }
    setForm(payLoad);
    setFormOutlet(outlets.find(e => e.id === payLoad.outlet));
    setStep(2);
    return true;
  };

  // Return to step 1 keeping the entered values (DelinkForm re-seeds from `form`).
  const handleBack = () => setStep(1);

  const handleOnInputChange = (inputValue) => {
    if (inputValue !== '') {
      dispatch(getCustomerNumbers(inputValue, '', false, true));
    }
    return inputValue;
  };

  const handleFinalize = payLoad => dispatch(delinkOutlet(payLoad));

  if (notFound) {
    return <Navigate to="/admin/outlets" replace/>;
  }

  const selectedOutlet = outletFetched ? outlet : undefined;

  const renderConfirmation = () => (
    <form onSubmit={(e) => { e.preventDefault(); handleFinalize(form); }}>
      <Card className="mg-top-20">
        <Card.Body>
          <ConfirmationField label="Outlet">
            {formOutlet.attributes.name}
          </ConfirmationField>
          <ConfirmationField label="User to be unlinked">
            {formOutlet.attributes.owner ? formOutlet.attributes.owner.name : formOutlet.attributes.user}
          </ConfirmationField>
          <ConfirmationField label="Data of delinking">
            {moment(form.delink_date)
              .format('DD/MM/YYYY')}
          </ConfirmationField>
          <ConfirmationField label="Description">
            {form.delink_reason}
          </ConfirmationField>
          <ConfirmationField label="Notification to be sent?">
            {form.supress_notification ? 'No' : 'Yes'}
          </ConfirmationField>
        </Card.Body>
        <Card.Footer>
          <Row>
            <div className="col-md-6 text-start">
              <Button variant="default" className="btn red-ghost-btn" onClick={handleBack}>
                Back
              </Button>
            </div>
            <div className="col-md-6 text-end">
              <Button variant="default" className="btn red-ghost-btn" type="submit">
                Confirm and Process
              </Button>
            </div>
          </Row>
        </Card.Footer>
      </Card>
    </form>
  );

  const content = step === 2 ? renderConfirmation() : (
    <DelinkForm
      fetched={fetched}
      handleSubmit={handleSubmit}
      onInputChange={handleOnInputChange}
      outlets={outlets}
      selectedOutlet={selectedOutlet}
      values={form}
    />
  );

  return (
    <div className="container-fluid">
      <Title>
        Delink
      </Title>

      {outletFetching ? (
        <p>
          Loading...
        </p>
      ) : content}
    </div>
  );
}
